/** First-launch identity, using the same name as the scoreboard and online rooms. */

import { EXPLICIT_NAME_ERROR, isExplicitName, normalizePlayerName } from "@lache/relay/names";
import { ListMenu } from "./menu";
import { DEFAULT_NAME, MAX_NAME_LENGTH } from "./scores";

export class UsernameScreen {
  readonly menu: ListMenu;
  private readonly input: HTMLInputElement;
  private readonly error: HTMLDivElement;
  private readonly back: HTMLButtonElement;
  private done: (() => void) | null = null;
  private firstLaunch = true;

  constructor(
    parent: HTMLElement,
    private readonly host: {
      getName(): string;
      hasName(): boolean;
      setName(name: string): void;
      present(menu: ListMenu, open: () => void): void;
    },
  ) {
    this.menu = new ListMenu(parent, {
      className: "username-menu",
      title: "Welcome to Lache",
      hint: "choose a username, then Enter to continue",
      back: "Back",
      onCancel: () => this.cancel(),
    });
    this.back = this.menu.el.querySelector<HTMLButtonElement>(".menu-back")!;
    const intro = document.createElement("p");
    intro.className = "username-intro";
    intro.textContent =
      "Choose your name for multiplayer. Your username and best times appear on the public leaderboard.";
    const label = document.createElement("label");
    label.className = "username-label";
    label.htmlFor = "player-username";
    label.textContent = "Username";
    this.input = document.createElement("input");
    this.input.id = "player-username";
    this.input.className = "username-input";
    this.input.type = "text";
    this.input.name = "username";
    this.input.setAttribute("autocomplete", "nickname");
    this.input.maxLength = MAX_NAME_LENGTH;
    this.input.spellcheck = false;
    this.input.setAttribute("autocapitalize", "off");
    this.input.enterKeyHint = "done";
    this.input.setAttribute("aria-describedby", "username-error");
    this.error = document.createElement("div");
    this.error.id = "username-error";
    this.error.className = "username-error";
    this.error.setAttribute("role", "status");
    this.input.addEventListener("input", () => this.clearError());
    this.input.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.isComposing) return;
      if (e.key === "Enter") {
        e.preventDefault();
        this.save();
      } else if (e.key === "Escape") {
        e.preventDefault();
        this.input.blur();
        this.cancel();
      }
    });
    this.input.addEventListener("keyup", (e) => e.stopPropagation());
    this.menu.content.append(intro, label, this.input, this.error);
  }

  open(firstLaunch: boolean, done: () => void): void {
    this.firstLaunch = firstLaunch;
    this.done = done;
    this.input.value = firstLaunch && !this.host.hasName() ? "" : this.host.getName();
    this.clearError();
    this.back.hidden = firstLaunch;
    this.menu.setTitle(firstLaunch ? "Welcome to Lache" : "Username");
    this.menu.setItems([
      { label: firstLaunch ? "Continue" : "Save", action: () => this.save() },
      ...(firstLaunch ? [{ label: `Play as ${DEFAULT_NAME}`, action: () => this.finish(DEFAULT_NAME) }] : []),
    ]);
    this.host.present(this.menu, () => this.menu.show(0));
  }

  private clearError(): void {
    this.input.removeAttribute("aria-invalid");
    this.error.textContent = "";
  }

  private save(): void {
    const explicit = isExplicitName(this.input.value);
    if (!normalizePlayerName(this.input.value) || explicit) {
      this.error.textContent = explicit
        ? EXPLICIT_NAME_ERROR
        : this.firstLaunch
          ? `Enter a username, or choose Play as ${DEFAULT_NAME}.`
          : "Enter a username.";
      this.input.setAttribute("aria-invalid", "true");
      this.input.focus();
      return;
    }
    this.finish(this.input.value);
  }

  private finish(name: string): void {
    this.host.setName(name);
    this.input.blur();
    const done = this.done;
    this.done = null;
    done?.();
  }

  private cancel(): void {
    if (this.firstLaunch) return;
    this.input.blur();
    const done = this.done;
    this.done = null;
    done?.();
  }
}
